/**
 * Order Notification Bell Component
 * 
 * Shows order update notifications (confirmed, ready, cancelled, etc.)
 * for DAC (consumer) users.
 */

import { useState, useEffect, useRef } from 'react';
import { Package, X, Check, Clock, AlertTriangle, CheckCircle, XCircle, Truck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { dacNotifications } from '../../utils/api';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

// Notification type icons and colors
const notificationConfig = {
  order_confirmed: {
    icon: CheckCircle,
    color: 'text-green-600',
    bgColor: 'bg-green-100',
    label: 'Confirmed'
  },
  delivery_time_updated: {
    icon: Clock,
    color: 'text-yellow-600',
    bgColor: 'bg-yellow-100',
    label: 'Time Updated'
  },
  order_ready: {
    icon: Package,
    color: 'text-blue-600',
    bgColor: 'bg-blue-100',
    label: 'Ready'
  },
  order_dispatched: {
    icon: Truck,
    color: 'text-purple-600',
    bgColor: 'bg-purple-100',
    label: 'Dispatched'
  },
  dasher_en_route: {
    icon: Truck,
    color: 'text-indigo-600',
    bgColor: 'bg-indigo-100',
    label: 'On The Way'
  },
  dasher_arriving: {
    icon: Truck,
    color: 'text-emerald-600',
    bgColor: 'bg-emerald-100',
    label: 'Arriving'
  },
  order_delivered: {
    icon: CheckCircle,
    color: 'text-green-600',
    bgColor: 'bg-green-100',
    label: 'Delivered'
  },
  order_cancelled: {
    icon: XCircle,
    color: 'text-red-600',
    bgColor: 'bg-red-100',
    label: 'Cancelled'
  },
  order_picked_up: {
    icon: CheckCircle,
    color: 'text-green-600',
    bgColor: 'bg-green-100',
    label: 'Picked Up'
  },
  default: {
    icon: Package,
    color: 'text-gray-600',
    bgColor: 'bg-gray-100',
    label: 'Update'
  }
};

export default function OrderNotificationBell() {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);
  const pollInterval = useRef(null);

  // Fetch notifications on mount and set up polling
  useEffect(() => {
    fetchNotifications();
    fetchUnreadCount();

    // Poll for new notifications every 30 seconds
    pollInterval.current = setInterval(() => {
      fetchUnreadCount();
    }, 30000);

    return () => {
      if (pollInterval.current) {
        clearInterval(pollInterval.current);
      }
    };
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const response = await dacNotifications.list(false, 20);
      setNotifications(response.data.notifications || []);
      setUnreadCount(response.data.unread_count || 0);
    } catch (error) {
      console.error('Failed to fetch order notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUnreadCount = async () => {
    try {
      const response = await dacNotifications.unreadCount();
      const newCount = response.data.unread_count || 0;
      
      // Show toast if new notification arrived
      if (newCount > unreadCount && unreadCount > 0) {
        toast.info('You have a new order update!', {
          action: {
            label: 'View',
            onClick: () => setIsOpen(true)
          }
        });
      }
      setUnreadCount(newCount);
    } catch (error) {
      console.error('Failed to fetch unread count:', error);
    }
  };

  const handleMarkRead = async (notificationId) => {
    try {
      await dacNotifications.markRead(notificationId);
      setNotifications(prev => 
        prev.map(n => 
          n.notification_id === notificationId ? { ...n, is_read: true } : n
        )
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await dacNotifications.markAllRead();
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
      toast.success('All notifications marked as read');
    } catch (error) {
      console.error('Failed to mark all as read:', error);
      toast.error('Failed to mark all as read');
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffMins < 1440) return `${Math.floor(diffMins / 60)}h ago`;
    return date.toLocaleDateString();
  };

  const getConfig = (type) => notificationConfig[type] || notificationConfig.default;

  const handleNotificationClick = (notification) => {
    const orderId = notification.data?.order_id;
    if (orderId) {
      handleMarkRead(notification.notification_id);
      setIsOpen(false);
      navigate(`/consumer/track/${orderId}`);
    }
  };

  const handleOpenDropdown = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      fetchNotifications();
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Button */}
      <Button
        variant="ghost"
        size="icon"
        className="relative"
        onClick={handleOpenDropdown}
        data-testid="order-notification-bell"
      >
        <Package className="w-5 h-5 text-gray-700" />
        
        {/* Unread Count Badge */}
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-orange-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center animate-pulse">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </Button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 sm:w-96 bg-white rounded-lg shadow-xl border z-50 max-h-[70vh] overflow-hidden">
          {/* Header */}
          <div className="p-3 border-b bg-gray-50 flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-gray-900">Order Updates</h3>
              <p className="text-xs text-gray-500">
                {unreadCount > 0 ? `${unreadCount} unread` : 'All caught up!'}
              </p>
            </div>
            {unreadCount > 0 && (
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={handleMarkAllRead}
                className="text-xs text-gray-500 hover:text-gray-700"
              >
                <Check className="w-3 h-3 mr-1" />
                Mark all read
              </Button>
            )}
          </div>

          {/* Notification List */}
          <div className="overflow-y-auto max-h-96">
            {loading ? (
              <div className="p-8 text-center text-gray-500">
                <div className="animate-spin w-6 h-6 border-2 border-gray-300 border-t-emerald-600 rounded-full mx-auto mb-2"></div>
                <p className="text-sm">Loading...</p>
              </div>
            ) : notifications.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <Package className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p className="font-medium">No order updates yet</p>
                <p className="text-sm mt-1">Order updates will appear here</p>
              </div>
            ) : (
              notifications.map((notification) => {
                const config = getConfig(notification.type);
                const IconComponent = config.icon;
                
                return (
                  <div
                    key={notification.notification_id}
                    className={`p-3 border-b hover:bg-gray-50 transition-colors cursor-pointer ${
                      !notification.is_read ? 'bg-orange-50' : ''
                    }`}
                    onClick={() => handleNotificationClick(notification)}
                    data-testid={`order-notification-${notification.notification_id}`}
                  >
                    <div className="flex items-start gap-3">
                      {/* Icon */}
                      <div className={`${config.bgColor} p-2 rounded-lg flex-shrink-0`}>
                        <IconComponent className={`w-4 h-4 ${config.color}`} />
                      </div>
                      
                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <p className="font-medium text-gray-900 text-sm truncate">
                            {notification.title}
                          </p>
                          {!notification.is_read && (
                            <Badge className="bg-orange-500 text-white text-xs px-1.5 py-0">
                              New
                            </Badge>
                          )}
                        </div>
                        <p className="text-xs text-gray-600 line-clamp-2">
                          {notification.body}
                        </p>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-xs text-gray-400 flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {formatTime(notification.sent_at)}
                          </span>
                          <Badge variant="outline" className="text-xs">
                            {config.label}
                          </Badge>
                        </div>
                      </div>
                      
                      {/* Mark as read button (only for unread) */}
                      {!notification.is_read && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="w-6 h-6 flex-shrink-0"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleMarkRead(notification.notification_id);
                          }}
                        >
                          <Check className="w-3 h-3" />
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Footer */}
          {notifications.length > 0 && (
            <div className="p-2 border-t bg-gray-50">
              <Button 
                variant="ghost" 
                className="w-full text-sm text-emerald-600 hover:text-emerald-700"
                onClick={() => {
                  navigate('/consumer/orders');
                  setIsOpen(false);
                }}
              >
                View All Orders
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
